import { lazy, Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "@remix-run/react";
import { X } from "lucide-react";
import {
  getConfig,
  getFlagUrl,
  isSupportedRegion,
  regionConfigs,
} from "custom-config/region.configs";
import { DEFAULT_REGION } from "~/lib/constants/generalConstants";
import {
  normalizeRegionCode,
  type IpLocationForSearchPath,
} from "~/lib/helpers/ipLocationSearchPathHelper";
import { useUserLocationStore } from "~/store/userLocation";
import { RegionConfig, useRegionConfig } from "~/store/regionConfigStore";
import { EmptyLazyFallback, loadLazyModule } from "~/lib/helpers/resolveLazyModule";
import { Button } from "~/components/ui/button";

const GlobalSettings = lazy(() =>
  loadLazyModule(
    () => import("~/components/blocks/Header/NavItems/GlobalSettings"),
    EmptyLazyFallback,
  ),
);

const DISMISS_STORAGE_KEY = "oh_region_mismatch_banner_dismissed";
const BANNER_HEIGHT_VAR = "--oh-region-banner-h";

type RegionMismatchBannerProps = {
  /** Current page / URL region (e.g. "au" while browsing /au/...). */
  pageRegion?: string | null;
  className?: string;
};

function resolvePageRegion(
  pageRegion?: string | null,
  pathname?: string,
): string {
  const fromProp = normalizeRegionCode(pageRegion || undefined);
  if (fromProp && isSupportedRegion(fromProp)) return fromProp;

  const firstSegment = pathname?.split("/").filter(Boolean)[0];
  const fromPath = normalizeRegionCode(firstSegment);
  if (fromPath && isSupportedRegion(fromPath)) return fromPath;

  return DEFAULT_REGION;
}

function setBannerHeightVar(px: number) {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, `${Math.max(0, px)}px`);
}

async function fetchIpLocation(): Promise<IpLocationForSearchPath | null> {
  try {
    const response = await fetch("/api/location");
    if (!response.ok) return null;
    return (await response.json()) as IpLocationForSearchPath;
  } catch {
    return null;
  }
}

export default function RegionMismatchBanner({
  pageRegion: pageRegionProp,
  className = "",
}: RegionMismatchBannerProps) {
  const { pathname } = useLocation();
  const rootRef = useRef<HTMLDivElement>(null);
  const storeLocation = useUserLocationStore((s) => s.location);
  const setLocation = useUserLocationStore((s) => s.setLocation);
  const { updateSelectedCurrency } = useRegionConfig();

  const [fetchedLocation, setFetchedLocation] =
    useState<IpLocationForSearchPath | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const location = storeLocation ?? fetchedLocation;

  const pageRegion = resolvePageRegion(pageRegionProp, pathname);
  const ipRegion = normalizeRegionCode(location?.countryCode);

  // Don't wait for root's deferred geo chrome — fetch immediately when store is empty.
  useEffect(() => {
    if (storeLocation?.countryCode) return;

    let cancelled = false;
    fetchIpLocation().then((loc) => {
      if (cancelled || !loc?.countryCode) return;
      setFetchedLocation(loc);
      setLocation(loc as Parameters<typeof setLocation>[0]);
    });

    return () => {
      cancelled = true;
    };
  }, [storeLocation?.countryCode, setLocation]);

  const shouldShow = Boolean(
    !dismissed &&
    location?.countryCode &&
    ipRegion &&
    isSupportedRegion(ipRegion) &&
    ipRegion !== pageRegion,
  );

  const ipRegionConfig = useMemo(() => {
    if (!shouldShow || !ipRegion) return null;
    return getConfig(ipRegion as keyof typeof regionConfigs) as RegionConfig & {
      city?: string;
      country?: string;
    };
  }, [shouldShow, ipRegion]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = sessionStorage.getItem(DISMISS_STORAGE_KEY);
      if (raw === "1" || (ipRegion && raw === ipRegion)) {
        setDismissed(true);
      }
    } catch {
      // sessionStorage may be unavailable
    }
  }, [ipRegion]);

  // Publish height so fixed geosearch chrome can sit below this banner.
  useLayoutEffect(() => {
    if (!shouldShow) {
      setBannerHeightVar(0);
      return;
    }

    const el = rootRef.current;
    if (!el) {
      setBannerHeightVar(0);
      return;
    }

    const apply = () => setBannerHeightVar(el.offsetHeight);
    apply();

    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(apply) : null;
    ro?.observe(el);

    return () => {
      ro?.disconnect();
      setBannerHeightVar(0);
    };
  }, [shouldShow, ipRegion]);

  if (!shouldShow || !ipRegionConfig || !ipRegion) return null;


  const regionLabel =
    (location && "country" in location ? location.country?.trim() : "") ||
    ipRegion.toUpperCase();
  const countryName = ipRegionConfig.country?.trim() || regionLabel;
  const switchHref = ipRegion === DEFAULT_REGION ? "/" : `/${ipRegion}`;

  const dismissForSession = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_STORAGE_KEY, "1");
    } catch {
      // ignore
    }
  };

  const handleSwitchClick = () => {
    updateSelectedCurrency(ipRegionConfig);
    dismissForSession();
  };

  return (
    <div
      ref={rootRef}
      className={`relative w-full bg-white ${className}`}
      role="region"
      aria-label={`Switch to OfficeHub ${regionLabel}`}
    >
      <div className="flex flex-col gap-3 border-b border-oh-secondary/20 bg-oh-secondary/10 px-4 py-3 pr-12 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-5 sm:pr-12">
        <div className="flex min-w-0 items-start gap-3 sm:items-center">
          <img
            src={getFlagUrl(ipRegion)}
            alt=""
            aria-hidden
            className="mt-0.5 h-9 w-9 shrink-0 rounded-full object-cover sm:mt-0"
            width={36}
            height={36}
          />
          <p className="text-sm leading-snug text-oh-black sm:text-[15px]">
            It appears you’re visiting from <strong>{countryName}</strong>.{" "}
            Would you like to switch to our <strong>{countryName}</strong>{" "}
            website for a more localized experience?
          </p>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-4 self-end sm:self-auto">
          <button
            type="button"
            className="text-sm font-semibold text-oh-secondary hover:underline"
            onClick={() => setSettingsOpen(true)}
          >
            Another region
          </button>
          <Button
            asChild
            className="bg-oh-secondary px-4 text-white hover:bg-oh-secondary/90"
          >
            <Link
              to={switchHref}
              prefetch="intent"
              onClick={handleSwitchClick}
              data-spinner="true"
            >
              Switch to {regionLabel}
            </Link>
          </Button>
        </div>
      </div>

      <button
        type="button"
        onClick={dismissForSession}
        className="absolute right-2 top-2 z-10 inline-flex h-8 w-8 items-center justify-center rounded-md text-oh-black/60 transition-colors hover:bg-black/5 hover:text-oh-black"
        aria-label="Dismiss region suggestion"
      >
        <X className="h-4 w-4" strokeWidth={2} />
      </button>

      {settingsOpen ? (
        <Suspense fallback={null}>
          <GlobalSettings
            region={pageRegion}
            open={settingsOpen}
            setOpen={setSettingsOpen}
            dialogOnly
            onRegionChosen={dismissForSession}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
